import type { MatrixWithDimensions } from '@mss_creator/shared';

export const WIZARD_STEPS = [1, 2, 3, 4] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];

export const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  1: 'Setup',
  2: 'Sujet & contexte',
  3: 'Dimensions',
  4: 'Récapitulatif',
};

/**
 * Derives which step a matrix's data completeness alone would resume on —
 * pure data-shape logic, blind to whether step 1 (setup review) has ever
 * been shown, and blind to `génération_en_cours`. Used by `deriveInitialStep`
 * below once it's decided step 1 should be skipped and the matrix isn't
 * actively generating. `matrixSummarySchema.currentStep` on the backend
 * (`matrix-repository.ts`'s `listMatrixSummariesByCompanyId`) hand-mirrors
 * `deriveInitialStep` as a whole, not just this function — including its
 * `génération_en_cours` → step 4 override — since there is no stored
 * `current_step` column on either side to keep the two in sync for free.
 */
export function deriveResumeStep(matrix: MatrixWithDimensions): WizardStep {
  const hasSujetEtContexte = Boolean(matrix.sujet?.trim()) && Boolean(matrix.contexte?.trim());
  if (!hasSujetEtContexte) return 2;
  if (matrix.dimensions.length === 0) return 3;
  return 4;
}

function isEmptyMatrix(matrix: MatrixWithDimensions): boolean {
  return !matrix.sujet?.trim() && !matrix.contexte?.trim() && matrix.dimensions.length === 0;
}

/**
 * Which step the wizard should mount on. A génération_en_cours matrix always
 * locks onto récapitulatif (step 4), regardless of its data. Otherwise: a
 * completely untouched matrix (nothing filled in yet — the "just created"
 * case, indistinguishable at the route level from "resumed") still opens on
 * step 1 so a first-time user always sees the company's singularity-setup
 * review; any matrix with even partial data skips straight to
 * `deriveResumeStep`'s result, since step 1 has already served its purpose
 * for that draft. Step 1 remains reachable afterward via the stepper's now
 * always-free backward navigation.
 */
export function deriveInitialStep(matrix: MatrixWithDimensions): WizardStep {
  if (matrix.state === 'génération_en_cours') return 4;
  if (isEmptyMatrix(matrix)) return 1;
  return deriveResumeStep(matrix);
}
